import { useEffect } from "react";

type SeoProps = {
  title: string;
  description: string;
  /** Path-only canonical, e.g. "/pricing". Defaults to current pathname. */
  canonicalPath?: string;
  ogImage?: string;
  noIndex?: boolean;
};

const SITE_ORIGIN = "https://storeledgerpro.com";

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-route SEO tags. Updates document.title, meta description,
 * canonical URL, and Open Graph / Twitter card tags on mount.
 */
export function Seo({
  title,
  description,
  canonicalPath,
  ogImage,
  noIndex,
}: SeoProps) {
  useEffect(() => {
    const path =
      canonicalPath ??
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonical = `${SITE_ORIGIN}${path}`;

    document.title = title;
    setMeta("name", "description", description);
    setCanonical(canonical);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", "website");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    if (ogImage) {
      setMeta("property", "og:image", ogImage);
      setMeta("name", "twitter:image", ogImage);
    }

    setMeta("name", "robots", noIndex ? "noindex,nofollow" : "index,follow");
  }, [title, description, canonicalPath, ogImage, noIndex]);

  return null;
}

export default Seo;